import React from 'react'
import {
  Badge,
  Box,
  Checkbox,
  FormField,
  TextInput,
  Select,
  ButtonText,
  Typography,
  Icon,
} from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import type { SurveyQuestion } from '../../../dtos/survey.dto'
import { SurveyQuestionType } from '../../../dtos/survey.dto'
import { RatingInput } from '../rating-input/rating-input'
import styles from './question-card.module.scss'
import { QuestionCardProps, QuestionCardTextProps } from './question-card.types'

export function QuestionCard({
  question,
  index,
  onRemove,
  onUpdate,
  onBlur,
  error,
  touched,
  editable = true,
  active = true,
  responseCount,
  disabledAt,
  isCollapsed = false,
  onToggleCollapsed,
  onDeactivate,
  typeLabel,
  questionLabel,
  requiredLabel,
  questionPreviewLabel,
  selectTypePlaceholder,
  ratingQuestionPlaceholder,
  freeTextQuestionPlaceholder,
  removeButton,
  defaultRatingMinLabel,
  defaultRatingMaxLabel,
  ratingTypeLabel,
  freeTextTypeLabel,
  activeBadgeLabel,
  deactivatedBadgeLabel,
  responseSummaryText,
  deactivateButtonLabel,
  activateButtonLabel,
  onActivate,
  allowedQuestionTypes,
}: Readonly<QuestionCardProps & QuestionCardTextProps>) {
  const isDeactivated = active === false || Boolean(disabledAt)
  const hasResponseCountMeta = typeof responseCount === 'number'
  const showHeader = isDeactivated || hasResponseCountMeta
  const showContent = !isDeactivated || !isCollapsed
  const hasResponses = (responseCount ?? 0) > 0
  const showRemoveButton = !hasResponses && !isDeactivated
  const showDeactivateButton = hasResponses && !isDeactivated
  const showActivateButton = isDeactivated
  const showQuestionActions =
    showActivateButton || showDeactivateButton || showRemoveButton

  const allQuestionTypes = [
    { value: SurveyQuestionType.Rating, label: ratingTypeLabel },
    { value: SurveyQuestionType.FreeText, label: freeTextTypeLabel },
  ]
  const questionTypes = allowedQuestionTypes
    ? allQuestionTypes.filter((t) =>
        allowedQuestionTypes.includes(t.value as SurveyQuestionType)
      )
    : allQuestionTypes
  const hideTypeSelector = questionTypes.length <= 1
  const handleTypeChange = (option: SelectOption) => {
    if (!editable) return
    const newType = option.value as SurveyQuestionType

    if (newType === SurveyQuestionType.Rating) {
      onUpdate({
        type: SurveyQuestionType.Rating,
        config: {
          min: 1,
          max: 10,
          minLabel: defaultRatingMinLabel,
          maxLabel: defaultRatingMaxLabel,
        },
      })
    } else {
      onUpdate({
        type: SurveyQuestionType.FreeText,
        placeholder: '',
      })
    }
  }

  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editable) return
    onUpdate({ label: e.target.value })
  }

  const handleQuestionTextBlur = () => {
    onBlur()
  }

  const handleRequiredChange = (checked: boolean) => {
    if (!editable) return
    onUpdate({ required: checked })
  }

  const showError = touched && error

  return (
    <Box className={styles.QuestionCardWrapper}>
      {showHeader &&
        (isDeactivated && onToggleCollapsed ? (
          <Box
            className={styles.QuestionCardHeaderClickable}
            role="button"
            tabIndex={0}
            onClick={onToggleCollapsed}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggleCollapsed()
              }
            }}
            aria-expanded={!isCollapsed}
          >
            <Badge variant="default">{deactivatedBadgeLabel}</Badge>
            {responseSummaryText && (
              <Typography
                variant="default"
                $color="gray600"
                className={styles.HeaderResponseText}
              >
                {responseSummaryText}
              </Typography>
            )}
            <Box className={styles.CollapseIcon}>
              <Icon
                name={isCollapsed ? 'chevron-down' : 'chevron-up'}
                size={5}
              />
            </Box>
          </Box>
        ) : (
          <Box className={styles.QuestionCardHeader}>
            <Badge variant={isDeactivated ? 'default' : 'positive'}>
              {isDeactivated ? deactivatedBadgeLabel : activeBadgeLabel}
            </Badge>
            {responseSummaryText && (
              <Typography
                variant="default"
                $color="gray600"
                className={styles.HeaderResponseText}
              >
                {responseSummaryText}
              </Typography>
            )}
          </Box>
        ))}

      {showContent && (
        <>
          {!hideTypeSelector && (
            <FormField label={typeLabel} necessity="required">
              <Box className={editable ? styles.Select : styles.SelectReadOnly}>
                <Select
                  defaultValue={questionTypes.find(
                    (t) => t.value === question.type
                  )}
                  options={questionTypes}
                  onChange={editable ? handleTypeChange : undefined}
                  placeholder={selectTypePlaceholder}
                  borderRadiiSize="large"
                  disabled={!editable}
                />
              </Box>
            </FormField>
          )}

          <Box className={styles.QuestionWrapper}>
            <FormField
              label={`${questionLabel} ${index + 1}`}
              necessity="required"
              status={showError ? 'danger' : 'default'}
              statusMessage={showError ? error : undefined}
            >
              <Box className={styles.QuestionInputWrapper}>
                <Box className={styles.QuestionInput}>
                  <TextInput
                    name={`question-${question.id}`}
                    value={question.label}
                    onChange={handleQuestionTextChange}
                    onBlur={handleQuestionTextBlur}
                    placeholder={
                      question.type === SurveyQuestionType.Rating
                        ? ratingQuestionPlaceholder
                        : freeTextQuestionPlaceholder
                    }
                    readOnly={!editable}
                    disabled={!editable}
                  />
                </Box>

                {showQuestionActions && (
                  <Box className={styles.QuestionActions}>
                    {showActivateButton && (
                      <Box className={styles.ActionButton}>
                        <ButtonText onClick={() => onActivate?.()}>
                          {activateButtonLabel}
                        </ButtonText>
                      </Box>
                    )}

                    {showDeactivateButton && (
                      <Box className={styles.ActionButton}>
                        <ButtonText onClick={() => onDeactivate?.()}>
                          {deactivateButtonLabel}
                        </ButtonText>
                      </Box>
                    )}

                    {showRemoveButton && (
                      <Box className={styles.ActionButton}>
                        <ButtonText onClick={onRemove}>
                          {removeButton}
                        </ButtonText>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </FormField>

            <Checkbox
              name={`required-${question.id}`}
              value={question.required ? 'true' : undefined}
              active={question.required}
              onChange={(e) => handleRequiredChange(e.currentTarget.checked)}
              label={requiredLabel}
              variant="default"
              disabled={!editable}
            />
          </Box>

          {question.type === SurveyQuestionType.Rating && (
            <RatingPreview
              question={question}
              questionPreviewLabel={questionPreviewLabel}
            />
          )}
        </>
      )}
    </Box>
  )
}

function RatingPreview({
  question,
  questionPreviewLabel,
}: Readonly<{
  question: Extract<SurveyQuestion, { type: SurveyQuestionType.Rating }>
  questionPreviewLabel: string
}>) {
  return (
    <Box className={styles.PreviewSection}>
      <Typography variant="h5" $color={'text_primary'}>
        {questionPreviewLabel}
      </Typography>
      <RatingInput
        min={question.config.min}
        max={question.config.max}
        minLabel={question.config.minLabel}
        maxLabel={question.config.maxLabel}
        disabled
      />
    </Box>
  )
}
